import './App.css';
import { useState, useEffect, useCallback } from 'react';
import DashboardHeader from './dashboardheader.js'
import VerticalNavigation from './VerticalNavigation.js'
import bhcalc from './bhcalc.js'

const emptyForm = { email: '', firstName: '', lastName: '', locationId: '', initialPassword: '' };
const emptyEditForm = { email: '', firstName: '', lastName: '', locationIds: [] };

function Partners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [editingPartner, setEditingPartner] = useState(null);
  const [editFormData, setEditFormData] = useState(emptyEditForm);
  const [editError, setEditError] = useState('');
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);

  const [deletingPartner, setDeletingPartner] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const fetchPartners = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await bhcalc.get('/partner');
      setPartners(data);
    } catch (err) {
      console.error('Error fetching partners:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      const { data } = await bhcalc.get('/location');
      setLocations(data);
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  }, []);

  useEffect(() => {
    fetchPartners();
    fetchLocations();
  }, [fetchPartners, fetchLocations]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setIsSubmitting(true);
    try {
      await bhcalc.post('/partner', {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        locationId: formData.locationId ? parseInt(formData.locationId) : undefined,
        initialPassword: formData.initialPassword || undefined
      });
      setShowAddModal(false);
      setFormData(emptyForm);
      fetchPartners();
    } catch (err) {
      console.error('Error creating partner:', err);
      if (err.response?.status === 400) setFormError('Invalid partner data. Please check your inputs.');
      else if (err.response?.status === 401) setFormError('Session expired. Please log in again.');
      else if (err.response?.status === 403) setFormError('You do not have permission to create partners.');
      else if (err.response?.status === 500) setFormError('Server error. Please try again later.');
      else setFormError('Unable to create partner. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowAddModal(false);
    setFormData(emptyForm);
    setFormError('');
  };

  const openEdit = (partner) => {
    setEditingPartner(partner);
    const existingLocationIds = partner.locations?.map(l => String(l.id))
      || (partner.locationId ? [String(partner.locationId)] : []);
    setEditFormData({
      email: partner.email || '',
      firstName: partner.firstName || '',
      lastName: partner.lastName || '',
      locationIds: existingLocationIds
    });
    setEditError('');
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditLocationChange = (e) => {
    const selected = Array.from(e.target.selectedOptions, opt => opt.value);
    setEditFormData(prev => ({ ...prev, locationIds: selected }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditError('');
    setIsEditSubmitting(true);
    try {
      await bhcalc.put(`/partner/${editingPartner.id}`, {
        email: editFormData.email,
        firstName: editFormData.firstName,
        lastName: editFormData.lastName,
        locationIds: editFormData.locationIds.map(id => parseInt(id))
      });
      setEditingPartner(null);
      fetchPartners();
    } catch (err) {
      console.error('Error updating partner:', err);
      if (err.response?.status === 400) setEditError('Invalid partner data. Please check your inputs.');
      else if (err.response?.status === 401) setEditError('Session expired. Please log in again.');
      else if (err.response?.status === 403) setEditError('You do not have permission to edit partners.');
      else if (err.response?.status === 404) setEditError('Partner not found.');
      else if (err.response?.status === 500) setEditError('Server error. Please try again later.');
      else setEditError('Unable to update partner. Please try again.');
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const handleEditCancel = () => {
    setEditingPartner(null);
    setEditError('');
  };

  const openDelete = (partner) => {
    setDeletingPartner(partner);
    setDeleteError('');
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    setDeleteError('');
    try {
      await bhcalc.delete(`/partner/${deletingPartner.id}`);
      setDeletingPartner(null);
      fetchPartners();
    } catch (err) {
      console.error('Error deleting partner:', err);
      if (err.response?.status === 401) setDeleteError('Session expired. Please log in again.');
      else if (err.response?.status === 403) setDeleteError('You do not have permission to delete partners.');
      else if (err.response?.status === 404) setDeleteError('Partner not found.');
      else if (err.response?.status === 500) setDeleteError('Server error. Please try again later.');
      else setDeleteError('Unable to delete partner. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="App min-h-screen">
      <div className="dashboard-wrapper flex flex-col min-h-screen">
        <div className="dashboard-header flex w-full max-h-24 h-14">
          <DashboardHeader />
        </div>
        <div className="w-full flex flex-wrap min-h-screen">
          <div className="dashboard-left-sidebar flex-none w-48 relative">
            <VerticalNavigation />
          </div>
          <div className="dashboard-right-main border-1 flex-auto p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-left">Partners</h2>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-3 py-1 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
              >
                <span className="text-xl">+</span>
                <span>Add Partner</span>
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">First Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Last Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 text-left">
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">Loading partners...</td>
                    </tr>
                  ) : partners.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">No partners found. Add your first partner above.</td>
                    </tr>
                  ) : (
                    partners.map((partner) => (
                      <tr key={partner.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{partner.firstName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{partner.lastName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{partner.email}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {partner.locations?.length
                            ? partner.locations.map(l => l.name).join(', ')
                            : partner.location?.name || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => openEdit(partner)}
                            className="text-indigo-600 hover:text-indigo-900 mr-3"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => openDelete(partner)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {partners.length > 0 && (
              <div className="mt-4">
                <span className="text-sm text-gray-600">Showing {partners.length} partner{partners.length !== 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed text-left inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Add New Partner</h2>
                <button
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                {formError && (
                  <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{formError}</div>
                )}
                <PartnerFormFields data={formData} onChange={handleChange} locations={locations} showPasswordField />
                <div className="flex justify-end space-x-3 pt-4 border-t mt-6">
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Adding...' : 'Add Partner'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingPartner && (
        <div className="fixed text-left inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit Partner</h2>
                <button
                  onClick={handleEditCancel}
                  disabled={isEditSubmitting}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleEditSubmit}>
                {editError && (
                  <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{editError}</div>
                )}
                <PartnerFormFields data={editFormData} onChange={handleEditChange} onLocationChange={handleEditLocationChange} locations={locations} multiSelectLocation />
                <div className="flex justify-end space-x-3 pt-4 border-t mt-6">
                  <button
                    type="button"
                    onClick={handleEditCancel}
                    disabled={isEditSubmitting}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isEditSubmitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isEditSubmitting ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingPartner && (
        <div className="fixed text-left inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Delete Partner</h2>
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete <span className="font-semibold">{deletingPartner.firstName} {deletingPartner.lastName}</span>? This action cannot be undone.
              </p>
              {deleteError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{deleteError}</div>
              )}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeletingPartner(null)}
                  disabled={isDeleting}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PartnerFormFields({ data, onChange, onLocationChange, locations, multiSelectLocation, showPasswordField }) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
        <input
          type="text"
          name="firstName"
          value={data.firstName}
          onChange={onChange}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter first name"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
        <input
          type="text"
          name="lastName"
          value={data.lastName}
          onChange={onChange}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter last name"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
        <input
          type="email"
          name="email"
          value={data.email}
          onChange={onChange}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter email address"
        />
      </div>
      {showPasswordField && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Initial Password *</label>
          <input
            type="password"
            name="initialPassword"
            value={data.initialPassword}
            onChange={onChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter initial password"
          />
        </div>
      )}
      <div>
        {multiSelectLocation ? (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">Locations</label>
            <select
              multiple
              value={data.locationIds}
              onChange={onLocationChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              size={Math.min(locations.length || 4, 6)}
            >
              {locations.map((location) => (
                <option key={location.id} value={String(location.id)}>
                  {location.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">Hold Ctrl / Cmd to select multiple locations</p>
          </>
        ) : (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <select
              name="locationId"
              value={data.locationId}
              onChange={onChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a location</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </>
        )}
      </div>
    </div>
  );
}

export default Partners;
